/*
 * EmployeeReceiptBlocker — Fullscreen blocking modal that stays on top
 * of the entire employee dashboard while there are pending "Termos de
 * Aceite" (document_receipts) dispatched by admin.
 *
 * Uses the reusable SignReceiptDialog so the OTP + PDF preview flow
 * matches the standalone "Assinar" button in the Código de Ética tab.
 */
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { API, useAuth } from "../App";
import SignReceiptDialog from "./SignReceiptDialog";
import { Button } from "./ui/button";
import { FileSignature, ShieldAlert, Loader2 } from "lucide-react";

export default function EmployeeReceiptBlocker() {
  const { token } = useAuth();
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [current, setCurrent] = useState(null);

  const refresh = useCallback(async () => {
    if (!token) { setLoading(false); return; }
    try {
      const r = await axios.get(`${API}/employee/document-receipts`, {
        params: { token }, timeout: 15000,
      });
      const items = (r.data?.items || []).filter(
        (it) => it.status === "pending" && !it.suspended,
      );
      setPending(items);
    } catch {
      setPending([]);
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { refresh(); }, [refresh]);

  // Refresh whenever the browser regains focus (e.g. employee got a new
  // dispatch while the app was in another tab).
  useEffect(() => {
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  if (loading || pending.length === 0) return null;

  // While the signing dialog is open, hide the orange blocker so it
  // doesn't cover the SignReceiptDialog (which renders in a portal).
  const blockerVisible = !current;

  return (
    <>
      <AnimatePresence>
        {blockerVisible && (
        <motion.div
          key="receipt-blocker"
          className="fixed inset-0 z-[9999] bg-slate-950/85 backdrop-blur-sm flex items-center justify-center p-4"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          data-testid="receipt-blocker"
        >
          <motion.div
            initial={{ scale: 0.9, y: 24, opacity: 0 }}
            animate={{ scale: 1, y: 0, opacity: 1 }}
            transition={{ type: "spring", damping: 20 }}
            className="max-w-lg w-full bg-white rounded-2xl shadow-2xl overflow-hidden"
          >
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 px-6 py-6 text-white">
              <div className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
                  <ShieldAlert className="w-8 h-8" />
                </div>
                <div>
                  <p className="text-xs font-medium uppercase tracking-wider opacity-90">
                    Ação obrigatória
                  </p>
                  <h2 className="text-xl font-bold">
                    {pending.length === 1
                      ? "Você tem 1 documento para assinar"
                      : `Você tem ${pending.length} documentos para assinar`}
                  </h2>
                </div>
              </div>
              <p className="text-sm mt-3 opacity-95">
                Precisamos que você leia e assine o(s) documento(s) abaixo antes de
                continuar usando o app. É rápido — vamos enviar um código no seu
                SMS ou E-mail.
              </p>
            </div>

            <div className="p-5 space-y-3">
              {pending.slice(0, 5).map((rec, idx) => (
                <div
                  key={rec.id}
                  className="p-3 border border-slate-200 rounded-lg bg-slate-50 flex items-center gap-3"
                  data-testid={`receipt-blocker-item-${rec.id}`}
                >
                  <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <FileSignature className="w-5 h-5 text-amber-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-slate-800 truncate">
                      {rec.document_title || "Documento"}
                    </p>
                    <p className="text-[11px] text-slate-500">
                      Enviado {rec.created_at
                        ? new Date(rec.created_at).toLocaleDateString("pt-BR")
                        : "recentemente"}
                    </p>
                  </div>
                  {idx === 0 && (
                    <span className="text-[10px] font-bold bg-amber-500 text-white px-2 py-0.5 rounded-full uppercase">
                      Próximo
                    </span>
                  )}
                </div>
              ))}
              {pending.length > 5 && (
                <p className="text-xs text-slate-500 text-center">
                  E mais {pending.length - 5} documento(s)…
                </p>
              )}
            </div>

            <div className="px-5 pb-5">
              <Button
                onClick={() => setCurrent(pending[0])}
                className="w-full bg-amber-600 hover:bg-amber-700 text-white text-base py-6 font-bold shadow-md"
                data-testid="receipt-blocker-start-btn"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                ) : (
                  <FileSignature className="w-5 h-5 mr-2" />
                )}
                Começar assinatura
              </Button>
            </div>
          </motion.div>
        </motion.div>
        )}
      </AnimatePresence>

      <SignReceiptDialog
        open={!!current}
        onOpenChange={(v) => {
          if (!v) {
            setCurrent(null);
            // Reload pending list — signing may have removed one.
            (async () => {
              try {
                const r = await axios.get(`${API}/employee/document-receipts`, {
                  params: { token }, timeout: 15000,
                });
                const remaining = (r.data?.items || [])
                  .filter((it) => it.status === "pending" && !it.suspended);
                setPending(remaining);
                // Auto-advance to the next pending receipt so the employee
                // signs them sequentially without extra clicks.
                if (remaining.length > 0) {
                  setTimeout(() => setCurrent(remaining[0]), 400);
                }
              } catch { /* fall back to manual refresh */ }
            })();
          }
        }}
        sourceKind={current?.source_kind}
        sourceId={current?.source_id}
        documentTitle={current?.document_title}
        onSigned={() => { /* refresh happens in onOpenChange */ }}
      />
    </>
  );
}
